import React, { useEffect, useState } from 'react'
import _uniqBy from 'lodash/uniqBy'
import _get from 'lodash/get'
import _toNumber from 'lodash/toNumber'
import { connect as connectRedux } from 'react-redux'
import { Map, fromJS } from 'immutable'
import { useLazyQuery } from '@apollo/client'
import { getBoundActions } from '../../actions/index'
import {
  updateUpstreamContextFromObject,
} from '../../services/upstream-context/index'
import { getPrimary } from '../../services/languages/index'
import {
  getFormattedContentData,
  CONTENT_IMAGE_LIST,
  createTitleGroupId,
  getFeaturedContent,
} from '../../services/content/index'
import { GET_EPISODES } from '../../services/detail/query'
import RowV3 from '../Row.v3/index'
import { STYLES } from '../Row.v3/utils'
import TileV2Graphql from '../Tile.v2/Tile.v2Graphql/index'
import SelectField from '../SelectField/index'
import { FILTER_APPLIED_GA4_EVENT } from '../../services/event-tracking/index'
import { getPlaceholderList, MOBILE_WIDTH } from './utils'
import DetailPageV3EpisodeItem from './DetailPageV3EpisodeItem'

/**
 * Detail page series episodes component
 * @param {Object} props - The props
 * @param {Map} props.detailData - The content details data
 * @returns {JSX.Element} DetailPageV3SeriesEpisodes
 */

function DetailPageV3SeriesEpisodes (props) {
  const {
    detailData = Map(),
    auth,
    user,
    staticText,
    upstreamContext,
    viewportWidth,
    setEventDefault,
    uiLocation,
  } = props

  const isMobile = viewportWidth < MOBILE_WIDTH

  const jwt = auth.get('jwt')
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const contentId = detailData.get('contentId')
  const contentType = detailData.get('contentType')
  const titleGroupId = detailData.get('titleGroupId') || createTitleGroupId(contentId, contentType)
  const featuredContent = getFeaturedContent({ data: detailData })

  const [getEpisodesData, { data, loading }] = useLazyQuery(GET_EPISODES)
  const episodesData = fromJS(_get(data, ['contentList', 'content', 0, 'contentEpisodes'], []) || [])
  const allEpisodes = _get(data, ['contentList', 'content', 0, 'allContentEpisodes'], []) || []
  const uniqueEpisodes = fromJS(_uniqBy(allEpisodes, 'seasonNumber')).sort(
    (a, b) => _toNumber(a.get('seasonNumber')) - _toNumber(b.get('seasonNumber')),
  )

  const loadingData = loading || !episodesData.size
  const placeholderList = getPlaceholderList()

  const [selectedSeason, setSelectedSeason] = useState(featuredContent
    ? featuredContent.get('seasonNumber', 1)
    : detailData.get('seasonNumber', 1) || 1)

  const seasonsOptions = uniqueEpisodes.map((episode) => fromJS({
    name: `${staticText.get('season')} ${episode.get('seasonNumber')}`,
    value: episode.get('seasonNumber'),
  }))

  const handleSeasonSelect = (item) => {
    const value = item.get('value')

    // GA4 event
    setEventDefault(FILTER_APPLIED_GA4_EVENT.mergeDeep({
      ui_element: 'Seasons Filter',
      ui_location: uiLocation,
      filter_setting: `Season ${value}`,
    }))

    setSelectedSeason(value)
  }

  useEffect(() => {
    if (titleGroupId) {
      getEpisodesData({
        variables: {
          language: userLanguage,
          imageLanguages: [userLanguage, '_primary'],
          titleGroupIds: [titleGroupId],
          seasonNumber: selectedSeason,
          imageType: CONTENT_IMAGE_LIST,
        },
        context: {
          jwt,
        },
      })
    }
  }, [titleGroupId, userLanguage, selectedSeason, jwt])

  const renderItem = (item, { index }) => {
    const tileData = getFormattedContentData(item)

    if (isMobile) {
      return (
        <DetailPageV3EpisodeItem
          key={tileData.get('contentId') || index}
          episode={tileData}
          uiLocation="series detail page"
          isCurrentEpisode={tileData.get('contentId') === featuredContent.get('contentId', -1)}
        />
      )
    }
    return (
      <TileV2Graphql
        key={tileData.get('contentId') || index}
        data={tileData}
        upstreamContext={updateUpstreamContextFromObject(
          upstreamContext,
          {
            contentType: tileData.get('contentType'),
            itemIndex: index,
            contentId: tileData.get('contentId'),
          },
        )}
        adminTitle="Series Episodes"
        index={index}
      />
    )
  }

  const items = loadingData ? placeholderList : episodesData

  return (
    <>
      <div className="detail-page-v3__series-episodes-filter">
        {seasonsOptions.size > 0
          && <SelectField
          options={seasonsOptions}
          onClick={handleSeasonSelect}
          defaultValue={selectedSeason}
        />}
      </div>
      <div className="detail-page-v3__series-episodes">
        <RowV3
          style={STYLES.S12234}
          asGrid={true}
        >
          {items.map((item, index) => renderItem(item, { index }))}
        </RowV3>
      </div>
    </>
  )
}

export default connectRedux((state) => ({
  auth: state.auth,
  user: state.user,
  upstreamContext: state.upstreamContext,
  viewportWidth: state.app.getIn(['viewport', 'width']),
  staticText: state.staticText.getIn(['data', 'detailPageV3', 'data'], Map()),
}), (dispatch) => {
  const actions = getBoundActions(dispatch)
  return {
    setEventDefault: actions.eventTracking.setEventDefault,
  }
})(DetailPageV3SeriesEpisodes)
